import { useEffect } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ThemeToggle } from "@/components/ThemeToggle";
import { DataSaverToggle } from "@/components/DataSaverToggle";
import { BottomNav, TopNav } from "@/components/BottomNav";
import { ScrollToTop } from "@/components/ScrollToTop";
import { AudiobookPlayer } from "@/components/AudiobookPlayer";
import { MurottalBar } from "@/components/quran/MurottalBar";
import { BadgeCelebration } from "@/components/BadgeCelebration";
import { useAuth } from "@/context/AuthContext";
import { useAudiobook } from "@/context/AudiobookContext";
import { useLang } from "@/context/LangContext";
import { renewDaily } from "@/lib/dailyReminders";
import { refreshManasikContent } from "@/lib/manasikContent";
import { renewItinerary } from "@/lib/itinerary";
import { AlertsProvider } from "@/context/AlertsContext";
import { PrayerScheduleProvider } from "@/context/PrayerScheduleContext";
import { API } from "@/lib/api";
import { motion } from "framer-motion";
import { Moon, User as UserIcon } from "lucide-react";

const BASE = API.replace(/\/api$/, "");

export const Layout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const audiobookOpen = useAudiobook()?.open;
  const { tc } = useLang();
  // On start and on language change: roll reminder windows forward (in the UI language) + pull admin content.
  useEffect(() => {
    renewDaily(tc).catch(() => {});
    renewItinerary(tc).catch(() => {});
    refreshManasikContent().catch(() => {});
  }, [tc]);

  return (
    <AlertsProvider>
    <PrayerScheduleProvider>
    <div className={`min-h-screen geo-watermark ${audiobookOpen ? "pb-56 md:pb-32" : "pb-24 md:pb-0"}`}>
      <header className="sticky top-0 z-40 glass border-b border-border/60">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-3">
          <button
            onClick={() => navigate("/")}
            data-testid="brand-logo"
            className="flex items-center gap-2"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-white">
              <Moon className="h-5 w-5" />
            </span>
            <div className="text-start leading-tight">
              <p className="font-serif text-lg font-semibold">{t("appName")}</p>
              <p className="text-[10px] text-muted-foreground -mt-0.5 hidden sm:block">{t("tagline")}</p>
            </div>
          </button>

          <TopNav />

          <div className="flex items-center gap-2">
            <DataSaverToggle className="hidden sm:flex" />
            <ThemeToggle />
            <LanguageSwitcher />
            <button
              onClick={() => navigate("/profile")}
              data-testid="header-avatar"
              aria-label={t("profile")}
              className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-primary/10 text-primary ring-1 ring-border/60 transition-transform hover:scale-105"
            >
              {user?.avatar ? (
                <img src={`${BASE}${user.avatar}`} alt="" className="h-full w-full object-cover" data-testid="header-avatar-image" />
              ) : (
                <UserIcon className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>
      </header>

      <motion.main
        key={window.location.pathname}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="mx-auto max-w-5xl px-4 py-5 sm:py-6"
      >
        <Outlet />
      </motion.main>

      <AudiobookPlayer />
      <MurottalBar />
      <BadgeCelebration />
      <ScrollToTop />
      <BottomNav />
    </div>
    </PrayerScheduleProvider>
    </AlertsProvider>
  );
};
